import { OFFER_PLAN_MONTHS, type OfferPlanMonths } from "./pricing";
import { SOCIAL_LINKS } from "../../social-links";
import { DOWNLOAD_LINKS } from "../../download-links";

export const offerMonthLabel = (months: OfferPlanMonths): string =>
  months === 1 ? "1 месяц" : months === 3 ? "3 месяца" : `${months} месяцев`;

const planMarkup = OFFER_PLAN_MONTHS.map((months, index) => `
  <article class="offer-plan" data-offer-plan="${months}" data-offer-index="${index}" aria-labelledby="offer-plan-${months}-title">
    <h3 class="offer-plan__heading" id="offer-plan-${months}-title">${offerMonthLabel(months)}</h3>
    <span class="offer-plan__badge" data-offer-recommended hidden>Самый выгодный</span>
    <p class="offer-plan__total"><output data-offer-total aria-label="Итоговая цена">—</output></p>
    <p class="offer-plan__monthly" data-offer-monthly>—</p>
    <p class="offer-plan__saving" data-offer-saving></p>
    <p class="offer-plan__trial">+7 дней бесплатно</p>
    <button type="button" class="offer-plan__select" disabled data-offer-select="${months}" aria-pressed="false" aria-labelledby="offer-plan-${months}-title"><span class="offer-sr-only">Выбрать тариф</span></button>
    <span class="offer-plan__availability" data-offer-plan-availability>Скоро</span>
  </article>`).join("");

const pricing = `
<div class="offer-pricing" data-offer-pricing data-pricing-state="idle">
  <header class="offer-heading"><h2 id="pricing-title">Выбери свой тариф</h2></header>
  <p class="offer-sr-only" data-offer-pricing-status role="status" aria-live="polite"></p>
  <div class="offer-pricing__grid" aria-label="Сроки подписки Blender Bridge">${planMarkup}</div>
  <aside class="offer-trial" aria-labelledby="offer-trial-title">
    <div class="offer-trial__copy">
      <h3 id="offer-trial-title">Неделя бесплатно</h3>
      <p class="offer-trial__description">Создавай свои первые сцены вместе с Blender Bridge</p>
    </div>
    <div class="offer-trial__action" data-offer-registration-target><button class="offer-action offer-action--primary" type="button" disabled>Начать 7 дней бесплатно</button><span>Скоро</span></div>
  </aside>
</div>`;

// Stable release link; no website rebuild is required for the next version.
const windowsDownload = `<div class="offer-install__availability">
          <a class="offer-action" data-offer-download="windows" href="${DOWNLOAD_LINKS.windows}" rel="noopener">Скачать для Windows</a>
        </div>
        <details class="offer-install__warning" data-offer-download-warning>
          <summary>Windows может показать предупреждение</summary>
          <p>Установщик пока без цифровой подписи. Если Windows заблокирует запуск скачанного здесь файла, нажми «Подробнее» → «Выполнить в любом случае».</p>
        </details>`;

const install = `
<div class="offer-install" data-offer-install>
  <header class="offer-heading"><h2 id="install-title">Скачай Blender Bridge</h2></header>
  <div class="offer-install__guide">
    <section class="offer-install__foundation" data-offer-install-card="0" aria-labelledby="install-foundation-title">
      <div class="offer-install__foundation-copy">
        <header class="offer-install__step-heading"><span class="offer-install__number" aria-hidden="true">01</span><span class="offer-install__step-label">Установи основу</span></header>
        <h3 id="install-foundation-title">Приложение Blender Bridge + MCP</h3>
        <p>Нужно для обоих способов подключения.</p>
        <p class="offer-install__setup">Установи Blender отдельно. Затем в Blender Bridge нажми «Добавить аддон».</p>
      </div>
      <div class="offer-install__actions">
        ${windowsDownload}
        <div class="offer-install__availability">
          <button type="button" class="offer-action offer-action--unavailable" data-offer-download="macos" disabled>Скачать для MacOS</button>
          <span>Скоро</span>
        </div>
      </div>
    </section>
    <section class="offer-install__choice" aria-labelledby="install-choice-title">
      <header class="offer-install__step-heading offer-install__choice-heading"><span class="offer-install__number" aria-hidden="true">02</span><h3 class="offer-install__step-label" id="install-choice-title">Выбери, где общаться с AI</h3></header>
      <div class="offer-install__paths">
        <article class="offer-install__path" data-offer-install-route="mcp" aria-labelledby="install-mcp-title">
          <header class="offer-install__route-heading"><h4 id="install-mcp-title">MCP в AI-приложении</h4></header>
          <p>Скопируй инструкцию для MCP из настроек Blender Bridge и отправь AI-агенту.</p>
          <p class="offer-install__detail">Расширение не требуется.</p>
        </article>
        <article class="offer-install__path" data-offer-install-card="1" data-offer-install-route="browser" aria-labelledby="install-browser-title">
          <header class="offer-install__route-heading"><h4 id="install-browser-title">В AI-чате в браузере</h4></header>
          <p>Расширение передаёт команды в Blender и возвращает результаты и снимки в чат.</p>
          <div class="offer-install__actions"><div class="offer-install__availability"><a class="offer-action" data-offer-download="extension" href="${DOWNLOAD_LINKS.extension}" target="_blank" rel="noopener noreferrer">Скачать Расширение</a></div></div>
        </article>
      </div>
    </section>
    <p class="offer-install__ready">Оставь Blender и Blender Bridge открытыми — и создавай сцену в разговоре с AI.</p>
  </div>
</div>`;

// Twitter, Telegram and Instagram retain the shipped product's icon geometry.
// YouTube uses the same 24-unit, currentColor silhouette language.
const icons = {
  twitter: '<path d="M18.244 2H21.308L14.615 9.653L22.488 22H16.322L11.492 14.498L4.928 22H1.862L9.021 13.819L1.472 2H7.794L12.16 8.846L18.244 2ZM17.168 20.052H18.866L6.871 3.846H5.047L17.168 20.052Z" fill="currentColor" />',
  telegram: '<path d="M20.6 5.4L17.8 18.6C17.6 19.5 17.1 19.8 16.3 19.4L12.4 16.6L10.5 18.5C10.3 18.7 10.1 18.9 9.7 18.9L10 14.9L17.3 8.3C17.6 8 17.2 7.8 16.8 8.1L7.8 13.8L4 12.6C3.2 12.3 3.2 11.8 4.2 11.4L19 5.7C19.7 5.4 20.4 5.8 20.6 5.4Z" fill="currentColor" />',
  youtube: '<path fill="currentColor" fill-rule="evenodd" d="M21.58 7.19a2.51 2.51 0 0 0-1.77-1.78C18.25 5 12 5 12 5s-6.25 0-7.81.41a2.51 2.51 0 0 0-1.77 1.78C2 8.76 2 12 2 12s0 3.24.42 4.81a2.51 2.51 0 0 0 1.77 1.78C5.75 19 12 19 12 19s6.25 0 7.81-.41a2.51 2.51 0 0 0 1.77-1.78C22 15.24 22 12 22 12s0-3.24-.42-4.81ZM10 15.04V8.96L15.2 12 10 15.04Z" />',
  instagram: '<rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" stroke-width="2" /><circle cx="12" cy="12" r="3.5" stroke="currentColor" stroke-width="2" /><circle cx="17.2" cy="6.8" r="1" fill="currentColor" />',
};

/*
 * До 16.09.2026 эти четыре значка были <button> без обработчика: нажатие не
 * делало ничего, хотя счётчик клика уже срабатывал. Значок соцсети обязан быть
 * ссылкой — иначе не работают средняя кнопка мыши, «открыть в новой вкладке»,
 * копирование адреса и озвучивание ссылки экранным диктором.
 */
const socialButtons = ([
  ["twitter", "Twitter"], ["telegram", "Telegram"], ["youtube", "YouTube"], ["instagram", "Instagram"],
] as const).map(([id, label]) => `<a class="offer-footer__social" href="${SOCIAL_LINKS[id]}" target="_blank" rel="noopener noreferrer me" data-offer-social="${id}" aria-label="${label}"><svg viewBox="0 0 24 24" fill="none" focusable="false" aria-hidden="true">${icons[id]}</svg></a>`).join("");

const saleTermsLink = import.meta.env.MODE === "ru"
  ? ""
  : '<a href="/terms-of-sale">Условия покупки</a>';

const footer = `
<footer class="offer-footer" data-offer-footer aria-labelledby="footer-title">
  <h2 class="offer-footer__brand" id="footer-title"><img src="/assets/img/logo.png" alt="" width="48" height="48" />Blender Bridge</h2>
  <nav class="offer-footer__legal" aria-label="Правовая информация">
    <a href="/privacy">Политика конфиденциальности</a>
    <a href="/terms">Условия использования</a>
    ${saleTermsLink}
    <a data-docs-link href="/docs/">Документация</a>
  </nav>
  <div class="offer-footer__socials" data-offer-socials aria-label="Социальные сети">${socialButtons}</div>
  <p class="offer-footer__copyright">© 2026 Blender Bridge</p>
</footer>`;

export const OFFER_SECTION_MARKUP = Object.freeze({ pricing, install, footer });
